import React, { useState, useEffect } from 'react';
import { useLead } from '../../contexts/LeadContext';
import { format } from 'date-fns';
import { supabase } from '../../config/supabase';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiSearch, 
  FiPhone, 
  FiMail, 
  FiEdit, 
  FiMessageSquare, 
  FiFilter, 
  FiUser, 
  FiBuilding, 
  FiMessageCircle, 
  FiArrowUp, 
  FiArrowDown, 
  FiCalendar,
  FiGlobe
} = FiIcons;

const LeadList = ({ onEditLead, onShowContacts }) => {
  const { leads, loading } = useLead();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [serviceFilter, setServiceFilter] = useState('');
  const [contactCounts, setContactCounts] = useState({});
  const [lastContactDates, setLastContactDates] = useState({});
  const [includeContactsInSearch, setIncludeContactsInSearch] = useState(false);
  const [contactSearchData, setContactSearchData] = useState({});
  const [isSearchingContacts, setIsSearchingContacts] = useState(false);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  // Fetch contact counts and last contact dates for each lead
  useEffect(() => {
    const fetchContactData = async () => {
      if (!leads.length) return;
      
      try {
        // Fetch all contacts first
        const { data, error } = await supabase
          .from('contacts')
          .select('id, lead_id, content, notes, subject, contact_date');

        if (error) throw error;

        // Count contacts per lead, store searchable content, and find last contact date
        const counts = {};
        const searchData = {};
        const lastDates = {};
        
        if (data) {
          data.forEach(contact => {
            // Update counts
            counts[contact.lead_id] = (counts[contact.lead_id] || 0) + 1;
            
            // Store searchable content
            if (!searchData[contact.lead_id]) {
              searchData[contact.lead_id] = [];
            }
            
            // Add contact data to searchable content
            const contactContent = [
              contact.content,
              contact.notes,
              contact.subject
            ].filter(Boolean).join(' ').toLowerCase();
            searchData[contact.lead_id].push(contactContent);
            
            // Track last contact date
            const contactDate = new Date(contact.contact_date);
            if (!lastDates[contact.lead_id] || contactDate > new Date(lastDates[contact.lead_id])) {
              lastDates[contact.lead_id] = contact.contact_date;
            }
          });
        }
        
        setContactCounts(counts);
        setContactSearchData(searchData);
        setLastContactDates(lastDates);
      } catch (err) {
        console.error('Error fetching contact data:', err);
      }
    };

    fetchContactData();
  }, [leads]);

  // Sort leads
  const sortedLeads = [...leads].sort((a, b) => {
    let comparison = 0;

    // Handle different field types
    switch (sortField) {
      case 'name':
      case 'email':
      case 'company_name':
      case 'service_requested':
      case 'lead_source':
      case 'status':
        // String comparison
        comparison = (a[sortField] || '').localeCompare(b[sortField] || '');
        break;
      case 'created_at':
      case 'updated_at':
        // Date comparison
        comparison = new Date(a[sortField]) - new Date(b[sortField]);
        break;
      case 'last_contact_date':
        // Last contact date comparison
        const aDate = lastContactDates[a.id] ? new Date(lastContactDates[a.id]) : new Date(0);
        const bDate = lastContactDates[b.id] ? new Date(lastContactDates[b.id]) : new Date(0);
        comparison = aDate - bDate;
        break;
      default:
        comparison = 0;
    }

    return sortDirection === 'asc' ? comparison : -comparison;
  });

  // Filter leads
  const filteredLeads = sortedLeads.filter(lead => {
    // Handle search term filtering with optional contact search
    let matchesSearch = true;
    if (searchTerm) {
      const leadContentMatches = 
        lead.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        lead.phone.includes(searchTerm) ||
        (lead.email && lead.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (lead.company_name && lead.company_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (lead.notes && lead.notes.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (lead.ai_reference_notes && lead.ai_reference_notes.toLowerCase().includes(searchTerm.toLowerCase()));

      // Check if we need to search in contacts
      if (!leadContentMatches && includeContactsInSearch) {
        const contactContent = contactSearchData[lead.id] || [];
        matchesSearch = contactContent.some(text => 
          text.includes(searchTerm.toLowerCase())
        );
      } else {
        matchesSearch = leadContentMatches;
      }
    }

    const matchesStatus = !statusFilter || lead.status === statusFilter;
    const matchesService = !serviceFilter || lead.service_requested === serviceFilter;

    return matchesSearch && matchesStatus && matchesService;
  });

  // Calculate statistics
  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'New').length,
    contacted: leads.filter(l => l.status === 'Contacted').length,
    scheduled: leads.filter(l => l.status === 'Scheduled').length,
    completed: leads.filter(l => l.status === 'Completed').length
  };

  // Get unique services for filter
  const uniqueServices = [...new Set(leads.map(l => l.service_requested).filter(Boolean))];
  
  // Get unique statuses for filter
  const uniqueStatuses = [...new Set(leads.map(l => l.status).filter(Boolean))];
  
  // Get unique lead sources for filter
  const uniqueSources = [...new Set(leads.map(l => l.lead_source).filter(Boolean))];

  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return 'bg-yellow-100 text-yellow-800';
      case 'Contacted': return 'bg-orange-100 text-orange-800';
      case 'Scheduled': return 'bg-purple-100 text-purple-800';
      case 'Completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const handleStatisticClick = (filterStatus) => {
    setStatusFilter(filterStatus);
    setSearchTerm('');
    setServiceFilter('');
  };

  const handleSearchWithContacts = async () => {
    if (!searchTerm) return;
    setIsSearchingContacts(true);
    try {
      // Already handled by the useEffect and filter logic
      console.log("Searching with contacts included");
    } catch (error) {
      console.error("Error searching in contacts:", error);
    } finally {
      setIsSearchingContacts(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <SafeIcon icon={FiArrowUp} className="h-3 w-3 ml-1" /> : 
      <SafeIcon icon={FiArrowDown} className="h-3 w-3 ml-1" />;
  };

  // Function to format website URL
  const formatWebsiteUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `https://${url}`;
  };

  useEffect(() => {
    if (includeContactsInSearch && searchTerm) {
      handleSearchWithContacts();
    }
  }, [includeContactsInSearch, searchTerm]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-electric-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <button
          onClick={() => handleStatisticClick('')}
          className="bg-blue-50 rounded-lg p-4 text-center hover:bg-blue-100 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
          <div className="text-sm text-blue-700">Total Leads</div>
        </button>
        <button
          onClick={() => handleStatisticClick('New')}
          className="bg-yellow-50 rounded-lg p-4 text-center hover:bg-yellow-100 transition-colors focus:outline-none focus:ring-2 focus:ring-yellow-500"
        >
          <div className="text-2xl font-bold text-yellow-600">{stats.new}</div>
          <div className="text-sm text-yellow-700">New</div>
        </button>
        <button
          onClick={() => handleStatisticClick('Contacted')}
          className="bg-orange-50 rounded-lg p-4 text-center hover:bg-orange-100 transition-colors focus:outline-none focus:ring-2 focus:ring-orange-500"
        >
          <div className="text-2xl font-bold text-orange-600">{stats.contacted}</div>
          <div className="text-sm text-orange-700">Contacted</div>
        </button>
        <button
          onClick={() => handleStatisticClick('Scheduled')}
          className="bg-purple-50 rounded-lg p-4 text-center hover:bg-purple-100 transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <div className="text-2xl font-bold text-purple-600">{stats.scheduled}</div>
          <div className="text-sm text-purple-700">Scheduled</div>
        </button>
        <button
          onClick={() => handleStatisticClick('Completed')}
          className="bg-green-50 rounded-lg p-4 text-center hover:bg-green-100 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
        >
          <div className="text-2xl font-bold text-green-600">{stats.completed}</div>
          <div className="text-sm text-green-700">Completed</div>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search leads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
            />
          </div>
          
          {/* Status filter with free-form input option */}
          <div className="relative">
            <input
              list="status-options"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              placeholder="Filter by status..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
            />
            <datalist id="status-options">
              <option value="">All Status</option>
              {uniqueStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </datalist>
          </div>
          
          {/* Service filter with free-form input option */}
          <div className="relative">
            <input
              list="service-options"
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              placeholder="Filter by service..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-electric-500 focus:border-electric-500"
            />
            <datalist id="service-options">
              <option value="">All Services</option>
              {uniqueServices.map(service => (
                <option key={service} value={service}>{service}</option>
              ))}
            </datalist>
          </div>
        </div>

        {/* Sort options */}
        <div className="flex flex-wrap items-center mb-4">
          <span className="text-sm text-gray-600 mr-2">Sort by:</span>
          <div className="flex flex-wrap gap-2">
            <button
              className={`text-sm px-3 py-1 rounded-md flex items-center ${
                sortField === 'company_name' ? 'bg-electric-100 text-electric-800' : 'bg-gray-100 hover:bg-gray-200'
              }`}
              onClick={() => handleSort('company_name')}
            >
              Company {renderSortIcon('company_name')}
            </button>
            <button
              className={`text-sm px-3 py-1 rounded-md flex items-center ${sortField === 'name' ? 'bg-electric-100 text-electric-800' : 'bg-gray-100 hover:bg-gray-200'}`}
              onClick={() => handleSort('name')}
            >
              Name {renderSortIcon('name')}
            </button>
            <button
              className={`text-sm px-3 py-1 rounded-md flex items-center ${sortField === 'status' ? 'bg-electric-100 text-electric-800' : 'bg-gray-100 hover:bg-gray-200'}`}
              onClick={() => handleSort('status')}
            >
              Status {renderSortIcon('status')}
            </button>
            <button
              className={`text-sm px-3 py-1 rounded-md flex items-center ${sortField === 'service_requested' ? 'bg-electric-100 text-electric-800' : 'bg-gray-100 hover:bg-gray-200'}`}
              onClick={() => handleSort('service_requested')}
            >
              Service {renderSortIcon('service_requested')}
            </button>
            <button
              className={`text-sm px-3 py-1 rounded-md flex items-center ${sortField === 'created_at' ? 'bg-electric-100 text-electric-800' : 'bg-gray-100 hover:bg-gray-200'}`}
              onClick={() => handleSort('created_at')}
            >
              Created Date {renderSortIcon('created_at')}
            </button>
            <button
              className={`text-sm px-3 py-1 rounded-md flex items-center ${sortField === 'last_contact_date' ? 'bg-electric-100 text-electric-800' : 'bg-gray-100 hover:bg-gray-200'}`}
              onClick={() => handleSort('last_contact_date')}
            >
              Last Contact Date {renderSortIcon('last_contact_date')}
            </button>
          </div>
        </div>

        {/* Include contacts in search checkbox */}
        <div className="flex items-center mb-4">
          <input
            id="includeContacts"
            type="checkbox"
            checked={includeContactsInSearch}
            onChange={(e) => setIncludeContactsInSearch(e.target.checked)}
            className="h-4 w-4 text-electric-600 focus:ring-electric-500 border-gray-300 rounded"
          />
          <label htmlFor="includeContacts" className="ml-2 text-sm text-gray-700">
            Include contact log history in search
          </label>
          {isSearchingContacts && (
            <div className="ml-2 animate-spin rounded-full h-4 w-4 border-b-2 border-electric-600"></div>
          )}
        </div>

        {(searchTerm || statusFilter || serviceFilter || includeContactsInSearch) && (
          <div className="flex items-center space-x-2 mb-4">
            <span className="text-sm text-gray-600">Active filters:</span>
            {searchTerm && (
              <span className="bg-electric-100 text-electric-800 px-2 py-1 rounded text-sm">
                Search: "{searchTerm}"
              </span>
            )}
            {statusFilter && (
              <span className="bg-electric-100 text-electric-800 px-2 py-1 rounded text-sm">
                Status: {statusFilter}
              </span>
            )}
            {serviceFilter && (
              <span className="bg-electric-100 text-electric-800 px-2 py-1 rounded text-sm">
                Service: {serviceFilter}
              </span>
            )}
            {includeContactsInSearch && searchTerm && (
              <span className="bg-electric-100 text-electric-800 px-2 py-1 rounded text-sm">
                Including contact logs
              </span>
            )}
            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('');
                setServiceFilter('');
                setIncludeContactsInSearch(false);
              }}
              className="text-sm text-electric-600 hover:text-electric-700 underline"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      {/* Results count */}
      <div className="flex justify-between items-center">
        <p className="text-gray-600">
          Showing {filteredLeads.length} of {leads.length} leads
        </p>
      </div>

      {/* Leads List */}
      {filteredLeads.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">
            {leads.length === 0 ? 'No leads found. Add your first lead!' : 'No leads match your search criteria.'}
          </p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredLeads.map(lead => (
            <div key={lead.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow p-6">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      {lead.company_name ? (
                        <>
                          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <SafeIcon icon={FiBuilding} className="h-5 w-5 mr-2 text-gray-400" />
                            {lead.company_name}
                          </h3>
                          <p className="text-gray-600 flex items-center mt-1">
                            <SafeIcon icon={FiUser} className="h-4 w-4 mr-2 text-gray-400" />
                            {lead.name}
                          </p>
                        </>
                      ) : (
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                          <SafeIcon icon={FiUser} className="h-5 w-5 mr-2 text-gray-400" />
                          {lead.name}
                        </h3>
                      )}
                    </div>
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(lead.status)}`}>
                      {lead.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm text-gray-600">
                    <div className="flex items-center">
                      <SafeIcon icon={FiPhone} className="h-4 w-4 mr-2 text-gray-400" />
                      <a href={`tel:${lead.phone}`} className="hover:text-electric-600">
                        {lead.phone}
                      </a>
                    </div>
                    {lead.email && (
                      <div className="flex items-center">
                        <SafeIcon icon={FiMail} className="h-4 w-4 mr-2 text-gray-400" />
                        <a href={`mailto:${lead.email}`} className="hover:text-electric-600">
                          {lead.email}
                        </a>
                      </div>
                    )}
                    {lead.website && (
                      <div className="flex items-center">
                        <SafeIcon icon={FiGlobe} className="h-4 w-4 mr-2 text-gray-400" />
                        <a 
                          href={formatWebsiteUrl(lead.website)} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="hover:text-electric-600 truncate"
                        >
                          {lead.website}
                        </a>
                      </div>
                    )}
                    {lead.service_requested && (
                      <div>
                        <span className="font-medium">Service:</span> {lead.service_requested}
                      </div>
                    )}
                    {lead.lead_source && (
                      <div>
                        <span className="font-medium">Source:</span> {lead.lead_source}
                      </div>
                    )}
                    <div>
                      <span className="font-medium">Created:</span> {format(new Date(lead.created_at), 'MMM d, yyyy')}
                    </div>
                    <div className="flex items-center">
                      <SafeIcon icon={FiCalendar} className="h-4 w-4 mr-2 text-gray-400" />
                      <span>
                        <span className="font-medium">Last Contact:</span> {lastContactDates[lead.id] 
                          ? format(new Date(lastContactDates[lead.id]), 'MMM d, yyyy')
                          : 'Never'}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <SafeIcon icon={FiMessageCircle} className="h-4 w-4 mr-2 text-gray-400" />
                      <span>
                        <span className="font-medium">Contact Logs:</span> {contactCounts[lead.id] || 0}
                      </span>
                    </div>
                  </div>

                  {lead.notes && (
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">Notes:</span> {lead.notes}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex flex-row lg:flex-col space-x-2 lg:space-x-0 lg:space-y-2 mt-4 lg:mt-0 lg:ml-6">
                  <button
                    onClick={() => onEditLead(lead)}
                    className="flex items-center justify-center px-3 py-2 bg-electric-600 text-white text-sm rounded-lg hover:bg-electric-700 transition-colors"
                  >
                    <SafeIcon icon={FiEdit} className="h-4 w-4 mr-1" /> Edit
                  </button>
                  <button
                    onClick={() => onShowContacts(lead)}
                    className="flex items-center justify-center px-3 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    <SafeIcon icon={FiMessageSquare} className="h-4 w-4 mr-1" />
                    Contact Logs {contactCounts[lead.id] ? `(${contactCounts[lead.id]})` : ''}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LeadList;